import React from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { Confirm, useFormGroups, useNotify, useUpdate } from 'react-admin';
import { Typography, Box } from '@mui/material';

import { shsModalsStyles, interFontStyle } from 'src/styles';
import CircleWarning from 'src/assets/CircleWarning';
import { ShsButton } from 'src/components/common';
import { Resource } from 'src/constants';
import { useTabbedFormNavigationEmployee } from 'src/utils/employees/navigation';

import { ModalProps } from '../../common/ShsFormToolbar';
import tabGroups from '../const/tab-groups';
import { goToFirstInvalidField } from 'src/utils/employees/formValidation';

const modalsStyles = {
    ...shsModalsStyles,
    '& .MuiPaper-root': {
        maxWidth: '588px',
        borderRadius: '20px'
    },
    '& .MuiDialogContent-root': {
        padding: 0,
        '> .shs-modal-body': {
            padding: '0 73px'
        }
    },
    '& .shs-modal-header': {
        maxWidth: '425px',
        margin: '3em auto 0 auto'
    },
    '& .shs-modal-footer': {
        padding: '20px 50px 50px',
        '> .MuiButton-root': {
            minWidth: '235px',
            padding: '13px 20px',
            '&.outline': {
                color: '#111928',
                border: '1px solid #DFE4EA'
            }
        }
    }
};

const modalsEditStyles = {
    ...modalsStyles,
    '& .shs-modal-footer': {
        padding: '20px 50px 50px',
        '> .MuiButton-root': {
            minWidth: '235px',
            padding: '13px 13px',
            '&.outline': {
                color: '#111928',
                border: '1px solid #DFE4EA'
            }
        }
    }
};

type ModalPropsEmployee = ModalProps & {
    isInactiveEmployee?: boolean;
    handleValidationError?: () => void;
    handleSaveBtnClick?: () => {};
    handleRedirect?: () => void;
    onSuccess?: () => void;
};

/**
 *  CancelEditEmployeeModal renders a confirmation modal when the user attempts to cancel
 *  an edit or adding of an employee entity in the form.
 */
export const CancelEditEmployeeModal: React.FC<ModalPropsEmployee> = ({
    isOpen,
    isEdit,
    isInactiveEmployee,
    onClose,
    handleRedirect = () => {},
    onSuccess = () => {}
}) => {
    const { isDirty } = useFormState();
    const { trigger, getValues, handleSubmit, setValue, setError, clearErrors } = useFormContext();
    const [update] = useUpdate();
    const notify = useNotify();

    let { navigateToTab } = useTabbedFormNavigationEmployee();
    const { getGroupFields } = useFormGroups();

    /**
     * Handles save button click event in cancel modal
     * Validates form fields and performs save operation if valid
     * If form is not valid, it navigates to the first invalid field
     */
    const handleSaveBtnClickCancelModal = async () => {
        // Mark submit attempt to allow cross-field validators to show errors
        setValue('__submitAttempt', true, { shouldDirty: false, shouldTouch: false, shouldValidate: false });

        // Synchronously enforce employment type selection when an education center is chosen
        const employeeCenters = (getValues('employeeCenters') as any[]) || [];
        employeeCenters.forEach((center, index) => {
            const hasCenterSelected = !!center?.educationCenterId;
            const isStaffMemberChecked = !!center?.staffMember;
            const isCivilLawChecked = !!center?.civilLawNatureContract;
            const staffField = `employeeCenters.${index}.staffMember` as any;
            const civilField = `employeeCenters.${index}.civilLawNatureContract` as any;
            if (hasCenterSelected && !isStaffMemberChecked && !isCivilLawChecked) {
                setError(staffField, { type: 'required', message: 'Оберіть тип працевлаштування' } as any);
                setError(civilField, { type: 'required', message: 'Оберіть тип працевлаштування' } as any);
            } else {
                clearErrors(staffField);
                clearErrors(civilField);
            }
        });

        // Navigate to the first invalid tab before checking validity to ensure fields are mounted/highlighted
        await goToFirstInvalidField(tabGroups, getGroupFields, trigger, getValues, setValue, navigateToTab);

        const isValid = await trigger();
        if (!isValid) {
            onClose();
            return;
        }

        await handleSubmit((data) => {
            update(
                Resource.EMPLOYEES,
                {
                    id: data.id,
                    data: { ...data },
                    meta: { useMock: false, method: `update` }
                },
                {
                    mutationMode: 'pessimistic',
                    onSuccess: () => {
                        handleRedirect();
                        onSuccess();
                    },
                    onError: (error: any) => {
                        handleRedirect();
                        notify(`Помилка: ${error?.message}`, {
                            type: 'error'
                        });
                    }
                }
            );
        })();
    };

    const getHeaderText = () => {
        if (isInactiveEmployee) {
            return 'Скасування активації працівника';
        }
        if (isEdit && !isDirty) {
            return 'Скасувати редагування працівника';
        }
        return 'Увага!';
    };

    const getBodyText = () => {
        if (!isEdit) {
            return 'Якщо ви повернетеся до списку працівників, додавання нового працівника буде скасовано і всі введені дані будуть втрачені.';
        }
        if (isInactiveEmployee) {
            return isDirty
                ? 'Ви внесли зміни у профіль працівника. Якщо ви скасуєте зараз, усі незбережені зміни буде втрачено. Чи бажаєте ви зберегти зміни перед виходом?'
                : 'Чи впевнені ви, що хочете скасувати активацію працівника?';
        }

        const base = 'Якщо ви повернетеся до профілю працівника, редагування працівника буде скасовано';
        return isDirty
            ? `${base} і всі введені дані будуть втрачені.`
            : `${base}. Чи впевнені ви що хочете скасувати редагування працівника?`;
    };

    const getCenseleButtonLabel = () => {
        if (!isEdit) {
            return 'Повернутися до списку';
        }
        if (isInactiveEmployee) {
            return 'Скасувати активацію';
        }
        if (isDirty) {
            return 'Перейти до профілю';
        }
        return 'Скасувати редагування';
    };

    const getBackButtonLabel = () => {
        if (!isEdit) {
            return 'Продовжити додавання';
        }
        if (isInactiveEmployee) {
            return 'Повернутися назад';
        }
        if (isDirty) {
            return 'Зберегти зміни';
        }
        return 'Продовжити редагування';
    };

    return (
        <>
            <Confirm
                isOpen={isOpen}
                loading={false}
                title={''}
                onConfirm={onClose}
                onClose={onClose}
                sx={isEdit ? modalsEditStyles : modalsStyles}
                content={
                    <>
                        <div className="shs-modal-header">
                            <Box sx={{ textAlign: 'center' }}>
                                <CircleWarning />
                            </Box>
                            <Typography variant="h5" component="h5" mb={2} mt={1} fontWeight="600" align="center">
                                {getHeaderText()}
                            </Typography>
                        </div>
                        <div className="shs-modal-body">
                            <Typography component="div" mb={3} align="center" sx={interFontStyle}>
                                {getBodyText()}
                            </Typography>
                        </div>
                        <div className="shs-modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <ShsButton
                                label={getCenseleButtonLabel()}
                                styleType="outline"
                                onClick={handleRedirect}
                                sx={{ flex: '1', marginRight: '1em' }}
                            />
                            <ShsButton
                                label={getBackButtonLabel()}
                                styleType="filled-danger"
                                onClick={isDirty && !isInactiveEmployee ? handleSaveBtnClickCancelModal : onClose}
                                sx={{ flex: '1' }}
                            />
                        </div>
                    </>
                }
            />
        </>
    );
};
